import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import type { StaffProfile } from '@/lib/types';
import {
  Avatar,
  Field,
  Modal,
  ModalHeader,
  Tag,
  inputStyle,
  useToast,
  Banner,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';

const ROLE_LABEL: Record<string, string> = {
  TENANT_ADMIN: '院長 / 總監',
  STORE_MANAGER: '店長',
  DOCTOR: '醫師',
  CONSULTANT: '諮詢顧問',
  ESTHETICIAN: '美容師',
  FRONT_DESK: '櫃檯',
};

export function ProfileMenu({
  name,
  role,
  onLogout,
}: {
  name: string;
  role: string;
  onLogout: () => void;
}) {
  const qc = useQueryClient();
  const { setUser, user } = useAuth();
  const { msg, ok, fail } = useToast();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: '', specialties: '', color: '#B97A65' });
  const ref = useRef<HTMLDivElement>(null);

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<StaffProfile>('/auth/profile'),
    enabled: open || editOpen,
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const startEdit = () => {
    const p = profile.data;
    setForm({
      name: p?.name ?? name,
      specialties: (p?.specialties ?? []).join('、'),
      color: p?.color ?? '#B97A65',
    });
    setOpen(false);
    setEditOpen(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch<StaffProfile>('/auth/profile', {
        name: form.name,
        specialties: form.specialties
          .split(/[,，、]/)
          .map((s) => s.trim())
          .filter(Boolean),
        color: form.color,
      }),
    onSuccess: (p) => {
      ok('個人資料已更新');
      if (user) setUser({ ...user, name: p.name });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      setEditOpen(false);
    },
    onError: fail,
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingLeft: 8,
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
        }}
      >
        <Avatar name={name} size={26} tone="rose" />
        <div className="topbar-profile-meta" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, textAlign: 'left' }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{name}</span>
          <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{ROLE_LABEL[role] ?? role}</span>
        </div>
        <Icon d={I.arrowDown} size={12} style={{ color: 'var(--ink-3)' }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 240,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 14, borderBottom: '1px solid var(--line-soft)' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{profile.data?.name ?? name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
              {profile.isLoading ? '載入中…' : profile.data?.email ?? '—'}
            </div>
            <div style={{ marginTop: 6 }}>
              <Tag tone="primary">{ROLE_LABEL[role] ?? role}</Tag>
            </div>
          </div>
          <button className="btn btn-ghost" style={menuItemStyle} onClick={startEdit}>
            <Icon d={I.user} size={13} /> 編輯個人資料
          </button>
          <button
            className="btn btn-ghost"
            style={{ ...menuItemStyle, color: 'var(--danger)' }}
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <Icon d={I.arrow} size={13} /> 登出
          </button>
        </div>
      )}

      <Modal open={editOpen} onClose={() => setEditOpen(false)} width={440}>
        <ModalHeader title="編輯個人資料" sub="更新顯示姓名、專長與代表色" onClose={() => setEditOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Banner msg={msg} />
          <Field label="顯示姓名 *">
            <input style={inputStyle} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="專長（逗號分隔）">
            <input style={inputStyle} value={form.specialties} onChange={(e) => setForm((f) => ({ ...f, specialties: e.target.value }))} placeholder="皮秒雷射、肉毒、玻尿酸" />
          </Field>
          <Field label="代表色">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} style={{ width: 44, height: 32, border: '1px solid var(--line)', borderRadius: 6, background: 'transparent', cursor: 'pointer' }} />
              <input style={{ ...inputStyle, flex: 1 }} className="num" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} />
            </div>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setEditOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!form.name.trim() || save.isPending} onClick={() => save.mutate()}>
            <Icon d={I.check} size={13} /> 儲存
          </button>
        </div>
      </Modal>
    </div>
  );
}

const menuItemStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  justifyContent: 'flex-start',
  padding: '10px 14px',
  borderRadius: 0,
  fontSize: 12.5,
} as const;
